/**
 * Utility for highlighting text matches safely
 *
 * Features:
 * - Case-insensitive matching
 * - Highlights ALL occurrences of the search term
 * - XSS-safe: escapes HTML before inserting mark tags
 * - Returns React-safe HTML string
 */

/**
 * Escapes HTML special characters to prevent XSS
 */
const escapeHtml = (text) => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

/**
 * Highlights all occurrences of a search term in a text string
 * @param {string} text - The original text to highlight
 * @param {string} query - The search query to highlight
 * @returns {string} - HTML string with <mark> tags around matches
 *
 * Example:
 *   highlightMatch("Hello world, hello again", "hello")
 *   Returns: "Hello world, hello again" with <mark> tags
 */
export function highlightMatch(text, query) {
  if (!text || !query) return escapeHtml(text);

  const trimmedQuery = query.trim();
  if (!trimmedQuery) return escapeHtml(text);

  // Escape the text first to prevent XSS
  const escapedText = escapeHtml(text);

  // Escape the query for regex (special characters)
  const escapedQuery = trimmedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Create case-insensitive regex to find ALL occurrences
  const regex = new RegExp(`(${escapedQuery})`, 'gi');

  // Replace all matches with <mark> tags
  const highlighted = escapedText.replace(regex, '<mark>$1</mark>');

  return highlighted;
}

/**
 * Alternative: Highlight with custom tag/class
 */
export function highlightMatchCustom(text, query, options = {}) {
  const { tag = 'mark', className = '' } = options;

  if (!text || !query) return escapeHtml(text);

  const trimmedQuery = query.trim();
  if (!trimmedQuery) return escapeHtml(text);

  const escapedText = escapeHtml(text);
  const escapedQuery = trimmedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedQuery})`, 'gi');

  const classNameAttr = className ? ` class="${className}"` : '';
  const highlighted = escapedText.replace(regex, `<${tag}${classNameAttr}>$1</${tag}>`);

  return highlighted;
}

export default highlightMatch;
